/**
 * Contains utilities to deal with ParamPanel.
 */

import SharedConfig from '@packages/systems/core/constants/SharedConfig';
import type {LocalizationState} from '@packages/systems/localization-types';
import {selectCmsItemsForCurrentLocale} from '@packages/systems/cms/client-selectors';
import {List} from 'immutable';
import {getItemLabel} from '@packages/shared/utils/ItemUtils';
import {
  isFauxDynContextField,
  createFieldPath,
} from '@packages/systems/dynamo/utils/ParamFieldPathUtils';
import {asFieldDisplayType} from '@packages/systems/dynamo/utils/CollectionFieldUtils';
import {memoize} from '@packages/utilities/memo';

import {Component, Expressions} from '@packages/systems/wfdl';
import {getGuidNullable} from '@packages/systems/core/utils/NativeIdUtils';
import {
  closestDynamoItemElement,
  closestDynamoWrapperElement,
  getDynamoItemElement,
} from '@packages/systems/dynamo/utils/DynamoElementUtils';
import {
  OPERATOR_EQ,
  OPERATOR_NE,
  OPERATOR_GT,
  OPERATOR_LT,
  OPERATOR_GTE,
  OPERATOR_LTE,
  OPERATOR_EXISTS,
  OPERATOR_NOT_EXISTS,
  OPERATOR_IDIN,
  OPERATOR_IDNIN,
  OPERATOR_TRUE,
  OPERATOR_FALSE,
  type OperatorId,
} from '@packages/systems/dynamo/utils/DynQueryUtils';

import type {
  BindingSource,
  ConsolidatedBindingContext,
} from '@packages/systems/dynamo/gateway';
import {CollectionStateType} from '@packages/systems/core/records/CollectionStateType';
import {TENSES_TO_HUMAN_PHRASES_MAP} from '@packages/systems/dynamo/constants';
import {type DynQueryParamType} from './DynQueryParam';
import type {ExpressionElement} from '@packages/systems/wfdl-ast';
import type {ReferencedCollectionItems} from '@packages/systems/cms/types';

const DYN_CONTEXT = SharedConfig.dynamicContextConstant;

const ALLOWED_TYPES_FOR_FIELD_OPTIONS = [
  'PlainText',
  'RichText',
  'Date',
  'Email',
  'Phone',
  'Number',
  'ImageRef',
  'Video',
  'Bool',
  'ItemRef',
  'ItemRefSet',
  'Link',
  'Option',
  'CommercePrice',
  'FileRef',
  'Set',
];

type TypeDependentElements = {
  element: ExpressionElement | null | undefined;
  dynamoItem: ExpressionElement | null | undefined;
  dynamoWrapper: ExpressionElement | null | undefined;
};

export function getTypeDependentElements({
  type,
  selectedNodeNativeId,
  currentComponent,
}: {
  type: 'query' | 'condition';
  selectedNodeNativeId: string | null;
  currentComponent: Component.ComponentType;
}): TypeDependentElements {
  const elementId = getGuidNullable(selectedNodeNativeId);
  const rootElement = Component.getRender(currentComponent);

  const element = elementId
    ? Expressions.getElementById(rootElement, elementId)
    : null;

  if (!element) {
    return {
      element: null,
      dynamoItem: null,
      dynamoWrapper: null,
    };
  }

  if (type === 'query') {
    // filter settings are available only on DynamoWrapper, DynamoList,
    // DynamoItem and Pagination elements
    const dynamoWrapper = closestDynamoWrapperElement(rootElement, element);

    const dynamoItem = dynamoWrapper
      ? getDynamoItemElement(dynamoWrapper)
      : null;

    return {element, dynamoItem, dynamoWrapper};
  } else {
    const dynamoItem = element
      ? closestDynamoItemElement(rootElement, element)
      : null;

    const dynamoWrapper = dynamoItem
      ? closestDynamoWrapperElement(rootElement, dynamoItem)
      : null;

    return {element, dynamoItem, dynamoWrapper};
  }
}

/** Consolidated binding context utilities */

export function getFieldPath(bindingSource: BindingSource | FauxBindingSource) {
  return createFieldPath(
    ...bindingSource.path.map((fragment) => fragment.slug)
  );
}

export function findBindingSourceByFieldPath(
  bindingSources: BindingSource[],
  fieldPath: string
): BindingSource | null {
  const bindingSourceIndex = indexBindingSourcesByFieldPath(bindingSources);
  return bindingSourceIndex[fieldPath] || null;
}

// @ts-expect-error - TS7006 - Parameter 'bindingSources' implicitly has an 'any' type.
const indexBindingSourcesByFieldPath = memoize((bindingSources) => {
  // @ts-expect-error - TS2347 - Untyped function calls may not accept type arguments. | TS7006 - Parameter 'index' implicitly has an 'any' type. | TS7006 - Parameter 'source' implicitly has an 'any' type.
  return bindingSources.reduce<Record<string, any>>((index, source) => {
    index[getFieldPath(source)] = source;
    return index;
  }, {});
});

export type FieldOptionType = {
  name: string;
  type?: string;
  innerType?: string;
  humanizedType?: string | null;
  value?: string;
  nested?: boolean;
  readOnly?: boolean;
};

export function toFieldOption(bindingSource: BindingSource): FieldOptionType {
  const {label, type, path, parentId} = bindingSource;

  const innerType =
    bindingSource.type === 'Set' ? bindingSource.meta.innerType : undefined;

  return {
    name: label,
    type,
    innerType,
    humanizedType: asFieldDisplayType(type, innerType),
    value: createFieldPath(...path.map(({slug}) => slug)),
    nested: parentId != null,
  };
}

const allowedTypes = new Set(ALLOWED_TYPES_FOR_FIELD_OPTIONS);
export function hasAllowedType(source: BindingSource) {
  return allowedTypes.has(source.type);
}

export function getSingularLabel(
  bindingContext: ConsolidatedBindingContext
): string {
  const {
    ui: {
      typeLabel: {default: defaultLabel, singular: singularLabel},
    },
  } = bindingContext;
  return singularLabel || defaultLabel;
}

type FauxIdBindingSource = {
  type: 'Id';
  label: BindingSource['label'];
  path: BindingSource['path'];
};

export type FauxBindingSource = FauxIdBindingSource;

export function getBindingSource(
  fieldPath: string,
  bindingContext: ConsolidatedBindingContext
): BindingSource | FauxBindingSource | null {
  if (isFauxDynContextField(fieldPath)) {
    return {
      type: 'Id',
      label: getSingularLabel(bindingContext),
      path: [{slug: '_id', type: 'Id'}],
    };
  }

  const bindingSources = bindingContext.getExpandedBindingSources();
  return findBindingSourceByFieldPath(bindingSources, fieldPath);
}

export const isReferenceBindingSource = (
  fieldPath: string,
  bindingSources: Array<BindingSource>
): boolean => {
  const bindingSource = findBindingSourceByFieldPath(bindingSources, fieldPath);

  return (
    bindingSource?.type === 'ItemRef' || bindingSource?.type === 'ItemRefSet'
  );
};

export function getHumanText({
  bindingContext,
  parentBindingContext,
  dynQueryParam,
  collectionState,
  localizationState,
}: {
  bindingContext: ConsolidatedBindingContext;
  parentBindingContext: ConsolidatedBindingContext | null;
  dynQueryParam: DynQueryParamType;
  collectionState: CollectionStateType;
  localizationState: LocalizationState;
}): string {
  const {fieldPath, operator, value, value2, value3} = dynQueryParam;

  const bindingSource = getBindingSource(fieldPath, bindingContext);

  if (!bindingSource) {
    return '';
  }

  const valueText = getValueText({
    bindingSource,
    operator,
    value,
    value2,
    value3,
    bindingContext,
    parentBindingContext,
    collectionState,
    localizationState,
  });

  const {label: bindingSourceLabel, type: bindingSourceType} = bindingSource;
  const singularName = getSingularLabel(bindingContext);

  const convertedOperator = convertItemRefFilterOperatorForRendering(
    bindingSourceType,
    operator
  );

  switch (convertedOperator) {
    case OPERATOR_EQ: {
      if (bindingSourceType === 'Id') {
        return `${singularName} is ${valueText}`;
      }

      return `${bindingSourceLabel} is ${valueText}`;
    }
    case OPERATOR_NE: {
      if (bindingSourceType === 'Id') {
        return `${singularName} is not ${valueText}`;
      }

      return `${bindingSourceLabel} is not ${valueText}`;
    }
    case OPERATOR_GT:
      return `${bindingSourceLabel} is greater than ${valueText}`;
    case OPERATOR_LT:
      return `${bindingSourceLabel} is less than ${valueText}`;
    case OPERATOR_GTE: {
      const comparator =
        bindingSourceType === 'Date' ? 'after' : 'greater than';
      return `${bindingSourceLabel} is ${comparator} or equal to ${valueText}`;
    }
    case OPERATOR_LTE: {
      const comparator = bindingSourceType === 'Date' ? 'before' : 'less than';
      return `${bindingSourceLabel} is ${comparator} or equal to ${valueText}`;
    }
    case OPERATOR_EXISTS:
      return `${bindingSourceLabel} is set`;
    case OPERATOR_NOT_EXISTS:
      return `${bindingSourceLabel} is not set`;
    case OPERATOR_IDIN:
      return `${bindingSourceLabel} contains ${valueText}`;
    case OPERATOR_IDNIN:
      return `${bindingSourceLabel} does not contain ${valueText}`;
    case OPERATOR_TRUE:
      return `${bindingSourceLabel} is on`;
    case OPERATOR_FALSE:
      return `${bindingSourceLabel} is off`;
    default:
      return '';
  }
}

function getValueText({
  bindingSource,
  operator,
  value,
  value2,
  value3,
  bindingContext,
  parentBindingContext,
  collectionState,
  localizationState,
}: {
  bindingSource: BindingSource | FauxBindingSource;
  operator: OperatorId;
  value: string | number | null;
  value2: string | number | null;
  value3: string | number | null;
  bindingContext: ConsolidatedBindingContext;
  parentBindingContext: ConsolidatedBindingContext | null;
  collectionState: CollectionStateType;
  localizationState: LocalizationState;
}) {
  if (value == null) {
    return '';
  }

  const stringValue = String(value);

  switch (bindingSource.type) {
    case 'Id': {
      return dynContextText(getSingularLabel(bindingContext));
    }
    case 'Date': {
      if (value !== 0 && value2) {
        // TODO: This will be the old style, in the future, i'm not sure excluding today will really make sense
        if (typeof value3 === 'boolean' || value3 === null) {
          return handleOldValue3ForDates({operator, value, value2, value3});
        }

        return handleNonTodayDates({value, value2, value3});
      }

      return 'today';
    }
    case 'Bool': {
      return stringValue === 'true' ? 'on' : 'false';
    }
    case 'Option': {
      const option = bindingSource.meta.options.find(
        (opt) => opt.id === stringValue
      );
      return option ? option.name : '(Unknown)';
    }
    case 'ItemRef':
    case 'ItemRefSet': {
      const items = selectCmsItemsForCurrentLocale({
        CollectionStore: collectionState,
        LocalizationStore: localizationState,
      });
      const itemRecord =
        typeof value === 'string'
          ? items.find((item) => item.get('_id') === value)
          : null;

      if (itemRecord) {
        return getItemLabel(itemRecord, collectionState.scheduledItems);
      }

      const dynContextValueText = getDynContextValueText(
        stringValue,
        parentBindingContext
      );

      return dynContextValueText || 'Deleted Item';
    }
    default: {
      return stringValue;
    }
  }
}

export function getDynContextValueText(
  value: string,
  bindingContext: ConsolidatedBindingContext | null
): string | null {
  if (isAnyDynContextValue(value)) {
    if (!bindingContext) {
      // If contextCollection is not found it was probably bound in a different collection page template
      // https://github.com/webflow/webflow/issues/14608#issuecomment-360276000
      return 'Different Context';
    }

    const singularName = getSingularLabel(bindingContext);

    if (isDynContextFieldValue(value)) {
      const fieldSlug = getDynContextFieldSlug(value);

      // Falling back to showing `bindingContextValue` to avoid UI crashing in cases of missing field.
      // There are lists that have filters using deleted fields, created before bug fix
      // https://github.com/webflow/webflow/pull/9417.
      const bindingSources = bindingContext.getExpandedBindingSources();
      const bindingSource = findBindingSourceByFieldPath(
        bindingSources,
        fieldSlug
      );
      const fieldName = bindingSource ? bindingSource.label : fieldSlug;
      const fieldType = bindingSource ? bindingSource.type : '';

      return dynContextFieldText(fieldName, fieldType, singularName);
    }

    if (isDynContextValue(value)) {
      return dynContextText(singularName);
    }
  }

  return null;
}

const idinToEq = (operator: OperatorId): OperatorId => {
  switch (operator) {
    case OPERATOR_IDIN: {
      return OPERATOR_EQ;
    }
    case OPERATOR_IDNIN: {
      return OPERATOR_NE;
    }
    default: {
      return operator;
    }
  }
};

const eqToIdin = (operator: OperatorId): OperatorId => {
  switch (operator) {
    case OPERATOR_EQ: {
      return OPERATOR_IDIN;
    }
    case OPERATOR_NE: {
      return OPERATOR_IDNIN;
    }
    default: {
      return operator;
    }
  }
};

export function convertItemRefFilterOperatorForPersisting(
  operator: OperatorId,
  value: string | number | null,
  parentBindingContext: ConsolidatedBindingContext | null
) {
  const fieldSlug =
    typeof value === 'string' && isDynContextFieldValue(value)
      ? getDynContextFieldSlug(value)
      : null;

  const bindingSources = parentBindingContext
    ? parentBindingContext.getExpandedBindingSources()
    : [];

  const bindingSource = fieldSlug
    ? findBindingSourceByFieldPath(bindingSources, fieldSlug)
    : null;

  return bindingSource && bindingSource.type === 'ItemRefSet'
    ? eqToIdin(operator)
    : idinToEq(operator);
}

export function convertItemRefFilterOperatorForRendering(
  fieldType: string,
  operator: OperatorId
) {
  return fieldType === 'ItemRef' ? idinToEq(operator) : operator;
}

const isAnyDynContextValue = (value?: string | null) =>
  value && new RegExp(`^${DYN_CONTEXT}($|\\..+$)`).test(value);

const isDynContextValue = (value?: string | null) => value === DYN_CONTEXT;

const isDynContextFieldValue = (value?: string | null) =>
  Boolean(value && new RegExp(`^${DYN_CONTEXT}\\..+`).test(value));

const getDynContextFieldSlug = (value: string): string =>
  value.replace(new RegExp(`^${DYN_CONTEXT}\\.`), '');

export const dynContextText = (collectionName: string) =>
  `Current ${collectionName}`;

export const dynContextFieldText = (
  fieldName: string,
  fieldType: string,
  collectionName: string
) => {
  const prefix = fieldType === 'ItemRefSet' ? 'Any' : '';
  const text = `${fieldName} of ${dynContextText(collectionName)}`;
  return prefix ? `${prefix} ${text}` : text;
};

// @ts-expect-error - TS7031 - Binding element 'operator' implicitly has an 'any' type. | TS7031 - Binding element 'value' implicitly has an 'any' type. | TS7031 - Binding element 'value2' implicitly has an 'any' type. | TS7031 - Binding element 'value3' implicitly has an 'any' type.
function handleOldValue3ForDates({operator, value, value2, value3}) {
  const whenText = operator === OPERATOR_GTE ? 'ago' : 'from now';
  const excludeTodayText = value3 ? ' excluding today' : '';

  return `${value} ${value2} ${whenText}${excludeTodayText}`;
}

// @ts-expect-error - TS7031 - Binding element 'value' implicitly has an 'any' type. | TS7031 - Binding element 'value2' implicitly has an 'any' type. | TS7031 - Binding element 'value3' implicitly has an 'any' type.
function handleNonTodayDates({value, value2, value3}) {
  // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'any' can't be used to index type '{ readonly FUTURE: "in the future"; readonly PAST: "in the past"; }'.
  return `${value} ${value2} ${TENSES_TO_HUMAN_PHRASES_MAP[value3]}`;
}

export type OperatorOption = {
  id: OperatorId;
  name: string;
};

// TODO (dynamo): Unify operator casing in the filter/condvis UI
export function getAvailableOperatorsByFieldType(
  fieldType: string
): OperatorOption[] {
  switch (fieldType) {
    case 'PlainText':
    case 'Option':
    case 'Email':
    case 'Phone':
      return [
        {name: 'Equals', id: OPERATOR_EQ},
        {name: 'Does Not Equal', id: OPERATOR_NE},
        {name: 'Is Set', id: OPERATOR_EXISTS},
        {name: 'Is Not Set', id: OPERATOR_NOT_EXISTS},
      ];

    case 'RichText':
    case 'ImageRef':
    case 'Video':
    case 'Link':
    case 'FileRef':
      return [
        {name: 'Is Set', id: OPERATOR_EXISTS},
        {name: 'Is Not Set', id: OPERATOR_NOT_EXISTS},
      ];

    case 'Number':
    case 'CommercePrice':
      return [
        {name: 'Equals', id: OPERATOR_EQ},
        {name: 'Does Not Equal', id: OPERATOR_NE},
        {name: 'Is Greater Than', id: OPERATOR_GT},
        {name: 'Is Less Than', id: OPERATOR_LT},
        {name: 'Is Set', id: OPERATOR_EXISTS},
        {name: 'Is Not Set', id: OPERATOR_NOT_EXISTS},
      ];

    case 'Bool':
      return [
        {name: 'is On', id: OPERATOR_TRUE},
        {name: 'is Off', id: OPERATOR_FALSE},
      ];

    case 'Date':
      return [
        {name: 'Is set', id: OPERATOR_EXISTS},
        {name: 'Is not set', id: OPERATOR_NOT_EXISTS},
        {name: 'Is after or equal to...', id: OPERATOR_GTE},
        {name: 'Is before or equal to...', id: OPERATOR_LTE},
      ];

    case 'ItemRef':
      return [
        {name: 'Equals', id: OPERATOR_EQ},
        {name: 'Does Not Equal', id: OPERATOR_NE},
        {name: 'Is Set', id: OPERATOR_EXISTS},
        {name: 'Is Not Set', id: OPERATOR_NOT_EXISTS},
      ];

    case 'ItemRefSet':
      return [
        {name: 'Contains', id: OPERATOR_IDIN},
        {name: 'Does Not Contain', id: OPERATOR_IDNIN},
        {name: 'Is Set', id: OPERATOR_EXISTS},
        {name: 'Is Not Set', id: OPERATOR_NOT_EXISTS},
      ];

    default:
      return [{name: 'Equals', id: OPERATOR_EQ}];
  }
}

const REFERENTIAL_DYN_QUERY_OPERATORS = [
  OPERATOR_EQ,
  OPERATOR_NE,
  OPERATOR_IDIN,
  OPERATOR_IDNIN,
];

export const getReferencedItemIds = (
  bindingSources: Array<BindingSource>,
  dynQueryParams: List<DynQueryParamType>
): ReferencedCollectionItems => {
  return dynQueryParams.reduce<Record<string, any>>((acc, param) => {
    const {fieldPath, value, operator} = param;

    if (!REFERENTIAL_DYN_QUERY_OPERATORS.includes(operator)) {
      return acc;
    }

    const bindingSource = findBindingSourceByFieldPath(
      bindingSources,
      fieldPath
    );

    if (!bindingSource) {
      return acc;
    }

    if (
      bindingSource.type !== 'ItemRef' &&
      bindingSource.type !== 'ItemRefSet'
    ) {
      return acc;
    }

    const {legacyReferencedCollectionId} = bindingSource.meta;

    const referencesSpecificCollectionItem = Boolean(
      legacyReferencedCollectionId &&
      typeof value === 'string' &&
      !isAnyDynContextValue(value)
    );

    if (!legacyReferencedCollectionId || !referencesSpecificCollectionItem) {
      return acc;
    }

    acc[legacyReferencedCollectionId] = acc[legacyReferencedCollectionId] || [];

    // Type is already checked in "referencesSpecificCollectionItem" but it doesn't get refined here
    if (typeof value === 'string') {
      acc[legacyReferencedCollectionId].push(value);
    }

    return acc;
  }, {});
};

export type HumanTexts = {
  fieldText: string | null;
  operatorText: string | null;
  valueText: string | null;
  operatorBoundSymbol: string;
  operatorNonBoundSymbol: string;
};

// Operators for which we do not show a value
const noValueOperators = new Set([
  OPERATOR_EXISTS,
  OPERATOR_NOT_EXISTS,
  OPERATOR_TRUE,
  OPERATOR_FALSE,
]);

const OPERATOR_BOUND_SYMBOLS: Record<OperatorId, string> = {
  [OPERATOR_EQ]: '=',
  [OPERATOR_NE]: '≠',
  [OPERATOR_GT]: '>',
  [OPERATOR_LT]: '<',
  [OPERATOR_GTE]: '≥',
  [OPERATOR_LTE]: '≤',
  [OPERATOR_TRUE]: '=',
  [OPERATOR_EXISTS]: 'is set',
  [OPERATOR_FALSE]: '=',
  [OPERATOR_NOT_EXISTS]: 'is set',
  [OPERATOR_IDIN]: '=',
  [OPERATOR_IDNIN]: '≠',
};

const OPERATOR_NON_BOUND_SYMBOLS: Record<OperatorId, string> = {
  [OPERATOR_EQ]: '=',
  [OPERATOR_NE]: '≠',
  [OPERATOR_GT]: '>',
  [OPERATOR_LT]: '<',
  [OPERATOR_GTE]: '≥',
  [OPERATOR_LTE]: '≤',
  [OPERATOR_TRUE]: 'is on',
  [OPERATOR_EXISTS]: 'is set',
  [OPERATOR_FALSE]: 'is off',
  [OPERATOR_NOT_EXISTS]: 'is not set',
  [OPERATOR_IDIN]: '=',
  [OPERATOR_IDNIN]: '≠',
};

export function getHumanTexts({
  bindingContext,
  parentBindingContext,
  dynQueryParam,
  collectionState,
  localizationState,
}: {
  bindingContext: ConsolidatedBindingContext;
  parentBindingContext: ConsolidatedBindingContext | null;
  dynQueryParam: DynQueryParamType;
  collectionState: CollectionStateType;
  localizationState: LocalizationState;
}): HumanTexts {
  const {fieldPath, operator, value, value2, value3} = dynQueryParam;

  const bindingSource = getBindingSource(fieldPath, bindingContext);
  if (!bindingSource) {
    return {
      fieldText: null,
      operatorText: null,
      valueText: null,
      operatorBoundSymbol: '',
      operatorNonBoundSymbol: '',
    };
  }

  const {label: bindingSourceLabel, type: bindingSourceType} = bindingSource;
  const singularName = getSingularLabel(bindingContext);
  const fieldText =
    bindingSourceType === 'Id' ? singularName : bindingSourceLabel;

  const convertedOperator = convertItemRefFilterOperatorForRendering(
    bindingSourceType,
    operator
  );

  const operatorText = getOperatorText(
    convertedOperator,
    bindingSourceType === 'Date'
  );

  const operatorBoundSymbol = OPERATOR_BOUND_SYMBOLS[convertedOperator] ?? '';
  const operatorNonBoundSymbol =
    OPERATOR_NON_BOUND_SYMBOLS[convertedOperator] ?? '';

  // The final value text will be empty for operators that do not show a value
  const finalValueText = noValueOperators.has(convertedOperator)
    ? ''
    : getValueText({
        bindingSource,
        operator,
        value,
        value2,
        value3,
        bindingContext,
        parentBindingContext,
        collectionState,
        localizationState,
      });

  return {
    fieldText,
    operatorText,
    valueText: finalValueText,
    operatorBoundSymbol,
    operatorNonBoundSymbol,
  };
}

// Maps an operator to its human-readable text.
function getOperatorText(operator: OperatorId, isDate: boolean): string {
  return OPERATOR_TEXTS[operator]?.(isDate) ?? '';
}

const OPERATOR_TEXTS: Partial<Record<OperatorId, (isDate: boolean) => string>> =
  {
    [OPERATOR_EQ]: () => 'is',
    [OPERATOR_NE]: () => 'is not',
    [OPERATOR_GT]: () => 'is greater than',
    [OPERATOR_LT]: () => 'is less than',
    [OPERATOR_GTE]: (isDate) =>
      isDate ? 'is after or equal to' : 'is greater than or equal to',
    [OPERATOR_LTE]: (isDate) =>
      isDate ? 'is before or equal to' : 'is less than or equal to',
    [OPERATOR_EXISTS]: () => 'is set',
    [OPERATOR_NOT_EXISTS]: () => 'is not set',
    [OPERATOR_IDIN]: () => 'contains',
    [OPERATOR_IDNIN]: () => 'does not contain',
    [OPERATOR_TRUE]: () => 'is on',
    [OPERATOR_FALSE]: () => 'is off',
  };

/* Exports for internal functions that have unit tests */
export const forTest = {getValueText} as const;
